import { Card, CardContent } from "@/components/ui/card";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import { OverviewCardHeader } from "@/lib/dashboard/OverviewCardHeader";
import { BigNumber, FitNumber, NUMBER_SCALE } from "@/components/ui/big-number";
import { useI18n } from "@/hooks/useI18n";
import { useMoneyCopy } from "@/lib/i18n/money-copy";

interface PaymentStatusCardProps {
  paidAmount: number;
  pendingAmount: number;
  /** Quota di `pendingAmount` gia' scaduta: tinge di rosso la coda della barra. */
  overdueAmount: number;
  /** Pagato + in attesa. Viene dai piani rate, non dal venduto. */
  totalAmount: number;
  /**
   * Commissioni Place2Be incassate fuori da Stripe e mai versate.
   *
   * Non e' una quota di `pendingAmount`: quella e' cassa che deve ancora arrivare dal
   * cliente, questa e' denaro gia' incassato da cui la fee non e' passata. Sta nella
   * stessa card perche' e' cassa, ma in un riquadro a parte e fuori dalla barra, che
   * rappresenta un'altra somma.
   */
  platformFeeDueAmount?: number | null;
  platformFeeDueCount?: number | null;
  /**
   * true per l'admin (`meta.scope === 'platform'`): lo stesso numero e' un credito verso
   * le agenzie invece che un debito proprio, e cambiare solo l'etichetta e' piu' onesto
   * che tenerne due versioni.
   */
  isPlatformScope?: boolean;
  formatCurrency: (value: number) => string;
}

/**
 * Lo stato dei pagamenti dell'intero perimetro, in una card sola accanto ai KPI.
 *
 * Tre numeri in fila -- totale, pagato, da pagare -- e sotto la barra che li disegna: il
 * totale e' nero perche' e' il fatto, gli altri due sono colorati perche' sono lo stato,
 * e a colpo d'occhio si legge il colore prima della cifra. La barra separa il pagato
 * (verde) dallo scaduto (rosso) invece di riempire solo la quota incassata: "quanto
 * manca" e "quanto e' in ritardo" sono due domande diverse e una barra sola le confondeva.
 *
 * Le commissioni da versare hanno il proprio riquadro in fondo: non
 * sono un terzo addendo del conto del cliente, sono una cosa da fare. In blu e non in
 * rosso, perche' in questa card il rosso e' gia' lo scaduto -- denaro che il cliente non
 * ha versato -- e questa invece e' una partita fra l'agenzia e Place2Be.
 */
export function PaymentStatusCard({
  paidAmount,
  pendingAmount,
  overdueAmount,
  totalAmount,
  platformFeeDueAmount,
  platformFeeDueCount,
  isPlatformScope = false,
  formatCurrency,
}: PaymentStatusCardProps) {
  const { t } = useI18n();
  const money = useMoneyCopy();

  const hasTotal = Number.isFinite(totalAmount) && totalAmount > 0;
  // Clamp: un saldo in eccesso non deve far sfondare la barra oltre il 100%.
  const percentOf = (value: number) =>
    hasTotal ? Math.min(100, Math.max(0, (value / totalAmount) * 100)) : 0;
  const paidPercent = percentOf(paidAmount);
  const overduePercent = Math.min(percentOf(overdueAmount), 100 - paidPercent);
  const hasOverdue = overdueAmount > 0;
  const hasFeeDue = (platformFeeDueAmount ?? 0) > 0;

  return (
    <Card className="h-full">
      <CardContent className="p-4 h-full flex flex-col gap-4">
        <OverviewCardHeader
          title={t("dashboard.overview.paymentStatus.column")}
          info={t("dashboard.overview.paymentStatus.infoOverview")}
          to="/payments"
        />

        {/*
          Senza totale la card non stampa "0": `formatCurrency` traduce un campo assente
          in un euro zero perfettamente credibile, e uno zero inventato e' peggio di un
          buco dichiarato. Il trattino copre due casi che a schermo si equivalgono --
          nessun piano di pagamento, oppure una RPC che non manda ancora questi campi.
        */}
        {!hasTotal ? (
          <div className="flex flex-col gap-1">
            <span className={`${NUMBER_SCALE.lg} font-semibold text-gray-400`}>—</span>
            <span className="text-xs text-gray-500">
              {t("dashboard.overview.paymentStatus.empty")}
            </span>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <BigNumber
                value={formatCurrency(totalAmount)}
                label={t(`${money}.total`)}
              />
              <BigNumber
                value={formatCurrency(paidAmount)}
                label={t(`${money}.paid`)}
                valueClassName="text-emerald-600"
              />
              <BigNumber
                value={formatCurrency(pendingAmount)}
                label={t(`${money}.pending`)}
                valueClassName={hasOverdue ? "text-red-600" : "text-amber-600"}
                info={
                  hasOverdue
                    ? t("dashboard.overview.paymentStatus.overdueOf", {
                        amount: formatCurrency(overdueAmount),
                      })
                    : undefined
                }
              />
            </div>

            <div
              className="h-2 w-full rounded-full bg-gray-100 overflow-hidden flex"
              role="img"
              aria-label={t("dashboard.overview.paymentStatus.barLabel", {
                paid: formatCurrency(paidAmount),
                total: formatCurrency(totalAmount),
              })}
            >
              <div className="h-full bg-emerald-500" style={{ width: `${paidPercent}%` }} />
              {/* Lo scaduto e' una quota dell'in attesa, non un terzo addendo: prende la
                  coda della barra, e il grigio che resta e' l'atteso non ancora scaduto. */}
              {overduePercent > 0 && (
                <div className="h-full bg-red-500" style={{ width: `${overduePercent}%` }} />
              )}
            </div>
          </>
        )}

        {/* Fuori dal ramo `hasTotal`: un debito aperto esiste anche su un perimetro senza
            piani rate attivi, e nasconderlo insieme alla barra lo farebbe sparire proprio
            quando e' l'unica cosa rimasta da guardare. */}
        {hasFeeDue && (
          <div className="mt-auto rounded-lg bg-blue-50 px-4 py-3">
            <div className="flex flex-col leading-tight min-w-0">
              <span className="text-sm text-blue-800">
                {t(
                  isPlatformScope
                    ? "dashboard.overview.paymentStatus.feeToRecover"
                    : "dashboard.overview.paymentStatus.feeToPay"
                )}
                {" "}
                <InfoTooltip
                  text={t(
                    isPlatformScope
                      ? "dashboard.overview.paymentStatus.feeToRecoverInfo"
                      : "dashboard.overview.paymentStatus.feeToPayInfo"
                  )}
                />
              </span>
              <FitNumber
                value={formatCurrency(platformFeeDueAmount ?? 0)}
                size="md"
                className="text-blue-800"
              />
              {(platformFeeDueCount ?? 0) > 0 && (
                <span className="text-xs text-blue-700">
                  {t("dashboard.overview.paymentStatus.feeCount", {
                    count: platformFeeDueCount ?? 0,
                  })}
                </span>
              )}
            </div>

          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PaymentStatusCard;
